import format from 'date-fns/format'
import { getPlanLabel } from './plan-label'

const DAY_SECONDS = 24 * 60 * 60
// 7-day trial, counted from day 0.
const LAST_TRIAL_DAY = 6
const TRIAL_PLANS = ['pro', 'basicPlus']

const unixSeconds = (value) => (Number.isFinite(value) ? value : null)
// Date only: midnight UTC of that day.
const utcDay = (seconds) => (seconds === null ? null : Math.floor(seconds / DAY_SECONDS) * DAY_SECONDS)
// Readable copy of the same UTC day. date-fns formats local time, so the UTC
// calendar date is rebuilt locally first.
const utcDayText = (seconds) => {
  if (seconds === null) return null
  const date = new Date(seconds * 1000)
  return format(new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()), 'dd.MM.yyyy')
}

// Same "cancel scheduled" test as the API's Stripe webhook.
const cancelScheduled = (subscription) => subscription.cancel_at_period_end === true || Boolean(subscription.cancel_at)

/**
 * Intercom contact attributes for trial emails. Every key is always sent
 * (null/false, never undefined) so a converted or lapsed trial gets cleared.
 * `_at` keys are unix seconds at midnight UTC, which Intercom stores as dates; 'Trial Day' is
 * only as fresh as the user's last Studio visit.
 */
export const getIntercomTrialAttributes = (user, now = Date.now()) => {
  const subscription = user?.subscriptionData?.data || {}
  const trialStart = unixSeconds(subscription.trial_start)
  const trialEnd = unixSeconds(subscription.trial_end)
  const onTrial = subscription.status === 'trialing'

  let trialDay = null
  if (onTrial && trialStart !== null) {
    const elapsedDays = Math.floor((now / 1000 - trialStart) / DAY_SECONDS)
    trialDay = Math.min(Math.max(elapsedDays, 0), LAST_TRIAL_DAY)
  }

  const canceledAt = unixSeconds(subscription.canceled_at)
  const canceledDuringTrial = subscription.status === 'canceled' && canceledAt !== null &&
    trialStart !== null && trialEnd !== null && canceledAt >= trialStart && canceledAt <= trialEnd
  const trialCanceled = (onTrial && cancelScheduled(subscription)) || canceledDuringTrial

  const plan = user?.subscriptionPlan
  const trialPlan = (onTrial || trialCanceled) && TRIAL_PLANS.includes(plan) ? getPlanLabel(plan).label : null

  return {
    'On Trial': onTrial,
    'Trial Day': trialDay,
    'Trial Plan': trialPlan,
    'Trial Canceled': trialCanceled,
    trial_started_at: utcDay(trialStart),
    trial_ends_at: utcDay(trialEnd),
    // Text, for reading only; filter on the `_at` dates.
    'Trial Start': utcDayText(trialStart),
    'Trial End': utcDayText(trialEnd)
  }
}
